import { createTheme, type Theme } from "@mui/material/styles";

export type ColorMode = "light" | "dark";

/**
 * MUI theme factory for the RefineAI redesign. Mirrors the CSS-variable tokens
 * in globals.css so MUI components (badges, TableSortLabel, inputs, …) match
 * the Tailwind surfaces in both light and dark. Text colors are driven by the
 * palette (not hardcoded) so they adapt to the mode.
 */
export function createAppTheme(mode: ColorMode): Theme {
  const isDark = mode === "dark";

  const tokens = isDark
    ? {
        canvas: "#100F0A",
        surface: "#1A1B12",
        border: "#2B2A1E",
        text: "#F2F0E4",
        textMuted: "#B8B5A2",
        accent: "#7C874E",
      }
    : {
        canvas: "#F1EEE2",
        surface: "#FFFFFF",
        border: "#E6E1D3",
        text: "#1A1B12",
        textMuted: "#55543F",
        accent: "#5C6638",
      };

  return createTheme({
    palette: {
      mode,
      primary: {
        main: tokens.accent,
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: "#86836F",
        contrastText: "#FFFFFF",
      },
      error: { main: "#B0492E" },
      warning: { main: "#C99A33" },
      success: { main: "#2E7D4F" },
      // MUI's default `info` is a stark blue; map it to the olive accent so
      // `severity="info"` Alerts and `color="info"` controls stay on-brand.
      info: { main: tokens.accent, contrastText: "#FFFFFF" },
      background: {
        default: tokens.canvas,
        paper: tokens.surface,
      },
      text: {
        primary: tokens.text,
        secondary: tokens.textMuted,
      },
      divider: tokens.border,
    },
    typography: {
      fontFamily:
        '"Schibsted Grotesk", system-ui, "Helvetica", "Arial", sans-serif',
      fontWeightRegular: 500,
      h1: { fontFamily: '"Young Serif", Georgia, serif', fontWeight: 500 },
      h2: { fontFamily: '"Young Serif", Georgia, serif', fontWeight: 500 },
      h3: { fontFamily: '"Young Serif", Georgia, serif', fontWeight: 500 },
      h4: { fontFamily: '"Young Serif", Georgia, serif', fontWeight: 500 },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { textTransform: "none", borderRadius: 8 },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { boxShadow: "0px 2px 4px -1px rgba(0,0,0,0.08)" },
        },
      },
    },
  });
}

// Default export kept for backward compatibility (light theme).
const theme = createAppTheme("light");
export default theme;
